import { motion } from 'framer-motion';
import {
  Box,
  Cloud,
  Layers,
  LayoutGrid,
  MessageSquareText,
  Sparkles,
  Upload,
  Users,
  Zap
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import './Features.css';

interface FeatureCardProps {
  title: string;
  items: string[];
  icon: React.ReactNode;
  delay?: number;
  highlighted?: boolean;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ title, items, icon, delay = 0, highlighted = false }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      className={`ftrs-card ${highlighted ? 'highlighted-card' : ''}`}
    >
      <div className={`ftrs-icon ${highlighted ? 'highlighted-icon' : ''}`}>{icon}</div>
      <h3 className={`ftrs-card-title ${highlighted ? 'highlighted-text' : ''}`}>{title}</h3>
      <ul className="ftrs-item-list">
        {items.map((item, index) => (
          <li key={index} className={`ftrs-list-item ${highlighted ? 'highlighted-text' : ''}`}>
            <span className="ftrs-bullet">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
};

const TimelineFeatureCard: React.FC<FeatureCardProps & { position: 'left' | 'right' }> = ({
  title,
  items,
  icon,
  delay = 0,
  position,
  highlighted = false
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: position === 'left' ? -50 : 50 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay }}
      className={`ftrs-timeline-card ftrs-timeline-card-${position}`}
    >
      <div className={`ftrs-timeline-card-content ${highlighted ? 'highlighted-timeline-card' : ''}`}>
        <div className={`ftrs-icon ${highlighted ? 'highlighted-icon' : ''}`}>{icon}</div>
        <h3 className={`ftrs-card-title ${highlighted ? 'highlighted-text' : ''}`}>{title}</h3>
        <ul className="ftrs-item-list">
          {items.map((item, index) => (
            <li key={index} className={`ftrs-list-item ${highlighted ? 'highlighted-text' : ''}`}>
              <span className="ftrs-bullet">•</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="ftrs-section-title">{children}</h2>
);

interface Feature {
  title: string;
  icon: React.ReactNode;
  items: string[];
  highlighted?: boolean;
}

const Features: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const savedTheme = localStorage.getItem("theme");
    return savedTheme === "dark";
  });

  // Sync with theme changes
  useEffect(() => {
    const checkTheme = () => {
      const savedTheme = localStorage.getItem("theme");
      setIsDarkMode(savedTheme === "dark");
    };

    checkTheme();
    window.addEventListener("storage", checkTheme);
    const interval = setInterval(checkTheme, 100);

    return () => {
      window.removeEventListener("storage", checkTheme);
      clearInterval(interval);
    };
  }, []);

  const features: Feature[] = [
    {
      title: 'Prompt-Based Layout Generation',
      icon: <MessageSquareText size={32} />,
      items: [
        'Natural language input for design specifications',
        'AI-powered layout suggestions and iterations',
        'Intuitive interface for design requirements',
      ],
      highlighted: false,
    },
    {
      title: 'Customizable Components',
      icon: <LayoutGrid size={32} />,
      items: [
        'Component-level customization options',
        'Real-time modifications to components',
        'Mix and match to create personalized layouts',
      ],
      highlighted: true,
    },
    {
      title: '2D and 3D Diagram Generation',
      icon: <Box size={32} />,
      items: [
        'Synchronized updates across views',
        'Interactive viewing perspectives',
        'Real-time visualization updates'
      ],
      highlighted: false,
    },
    {
      title: 'AI Integration',
      icon: <Sparkles size={32} />,
      items: [
        'AI-powered design optimization',
        'Smart space utilization suggestions',
        'Adaptive customization'
      ],
      highlighted: true,
    },
    {
      title: 'Export and Sharing Options',
      icon: <Upload size={32} />,
      items: [
        'Shareable project links',
        'Collaborative team workspaces',
        'Export to PDF, CAD, and STL formats'
      ],
      highlighted: false,
    },
    {
      title: 'Cloud Storage',
      icon: <Cloud size={32} />,
      items: [
        'Secure project cloud storage',
        'Automatic version control',
        'Cross-device accessibility'
      ],
      highlighted: true,
    },
  ];

  /* Simplified Upcoming Features to match clean aesthetic */
  const upcomingFeatures: Feature[] = [
    {
      title: 'AR/VR Integration',
      icon: <Layers size={32} />,
      items: ['Immersive 3D walkthroughs', 'Real-time AR overlay', 'VR support'],
      highlighted: false,
    },
    {
      title: 'Automated Cost Estimation',
      icon: <Zap size={32} />,
      items: ['Dynamic cost calculation', 'Budget optimization', 'Material breakdowns'],
      highlighted: true,
    },
    {
      title: 'Collaborative Multi-User Editing',
      icon: <Users size={32} />,
      items: ['Live multi-user editing', 'Change tracking', 'Team communication'],
      highlighted: false,
    },
  ];

  return (
    <div className={`features-page ${isDarkMode ? 'dark-mode' : ''}`}>

      {/* Hero Section */}
      <section className="features-hero">
        <h1 className="features-page-title">
          Tools for <span className="highlight-orange">Precision</span> <br />
          and <span className="highlight-orange">Productivity</span>
        </h1>
        <p className="features-page-subtitle">
          Design without limits. Explore the platform that eliminates<br />
          errors and ensures guaranteed project integrity.
        </p>
      </section>

      {/* Breadcrumb */}
      <div className="features-breadcrumb">
        <a href="/">Home</a> &gt; <span>Features</span>
      </div>

      <section id="what-we-offer" className="features-section-offer">
        <SectionTitle>What We Offer?</SectionTitle>
        <div className="features-grid">
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              {...feature}
              delay={0.1 + index * 0.1}
              highlighted={feature.highlighted}
            />
          ))}
        </div>
      </section>

      {/* Upcoming Features - Timeline Layout */}
      <section className="features-section-upcoming">
        <SectionTitle>Upcoming Features</SectionTitle>
        <p className="features-subtitle-small">
          Exciting new functionalities coming soon!
        </p>

        <div className="features-timeline-container">
          <div className="features-timeline-line"></div>

          <div className="features-timeline-nodes">
            {upcomingFeatures.map((_, i) => (
              <div key={i} className="features-timeline-node"></div>
            ))}
          </div>

          {upcomingFeatures.map((feature, index) => (
            <div key={index} className={index % 2 !== 0 ? 'pos1' : 'pos-left-wrapper'}>
              <TimelineFeatureCard
                position={index % 2 === 0 ? 'left' : 'right'}
                title={feature.title}
                icon={feature.icon}
                items={feature.items}
                delay={0.2 + (index * 0.1)}
                highlighted={feature.highlighted}
              />
            </div>
          ))}
        </div>
      </section>

      <div className="features-bottom-space"></div>
    </div>
  );
};

export default Features;